import { useState, useMemo } from 'react';
import { FiSearch, FiChevronUp, FiChevronDown, FiEdit2, FiTrash2 } from 'react-icons/fi';

export default function DataTable({
  columns = [],
  data = [],
  onEdit,
  onDelete,
  searchable = true,
  filterable = true,
}) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter((row) =>
      columns.some((col) => {
        const val = row[col.key];
        return val != null && String(val).toLowerCase().includes(q);
      })
    );
  }, [data, search, columns]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = a[sortKey] ?? '';
      const bVal = b[sortKey] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const paginated = sorted.slice(page * perPage, (page + 1) * perPage);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const hasActions = !!onEdit || !!onDelete;

  return (
    <div style={styles.wrapper}>
      {/* Toolbar */}
      <div style={styles.toolbar}>
        {searchable && (
          <div style={styles.searchBox}>
            <FiSearch size={16} color="#B2AB9C" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={handleSearchChange}
              style={styles.searchInput}
            />
          </div>
        )}
        {filterable && (
          <div style={styles.perPageWrap}>
            <span style={styles.perPageLabel}>Show</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(0);
              }}
              style={styles.perPageSelect}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={styles.tableScroll}>
        <table style={styles.table}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={styles.th}
                  onClick={() => handleSort(col.key)}
                >
                  <span style={styles.thContent}>
                    {col.label}
                    {sortKey === col.key && (
                      sortDir === 'asc' ? (
                        <FiChevronUp size={14} />
                      ) : (
                        <FiChevronDown size={14} />
                      )
                    )}
                  </span>
                </th>
              ))}
              {hasActions && <th style={styles.th}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (hasActions ? 1 : 0)}
                  style={styles.emptyCell}
                >
                  No records found
                </td>
              </tr>
            ) : (
              paginated.map((row, idx) => (
                <tr
                  key={row._id || idx}
                  style={idx % 2 === 1 ? styles.stripedRow : undefined}
                >
                  {columns.map((col) => (
                    <td key={col.key} style={styles.td}>
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </td>
                  ))}
                  {hasActions && (
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        {onEdit && (
                          <button
                            style={styles.actionBtn}
                            onClick={() => onEdit(row)}
                            title="Edit"
                          >
                            <FiEdit2 size={15} />
                          </button>
                        )}
                        {onDelete && (
                          <button
                            style={{ ...styles.actionBtn, color: '#C0392B' }}
                            onClick={() => onDelete(row)}
                            title="Delete"
                          >
                            <FiTrash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={styles.pagination}>
        <span style={styles.pageInfo}>
          Showing {sorted.length === 0 ? 0 : page * perPage + 1}
          {' - '}
          {Math.min((page + 1) * perPage, sorted.length)} of {sorted.length}
        </span>
        <div style={styles.pageButtons}>
          <button
            style={styles.pageBtn}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Previous
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i;
            } else if (page < 3) {
              pageNum = i;
            } else if (page > totalPages - 4) {
              pageNum = totalPages - 5 + i;
            } else {
              pageNum = page - 2 + i;
            }
            return (
              <button
                key={pageNum}
                style={{
                  ...styles.pageBtn,
                  ...(page === pageNum ? styles.pageBtnActive : {}),
                }}
                onClick={() => setPage(pageNum)}
              >
                {pageNum + 1}
              </button>
            );
          })}
          <button
            style={styles.pageBtn}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    background: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    gap: '12px',
    flexWrap: 'wrap',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#F5F0E8',
    borderRadius: '8px',
    padding: '8px 12px',
    flex: '1',
    maxWidth: '320px',
  },
  searchInput: {
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontSize: '0.85rem',
    color: '#2B362E',
    width: '100%',
    padding: 0,
  },
  perPageWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  perPageLabel: {
    fontSize: '0.8rem',
    color: '#6b6b6b',
  },
  perPageSelect: {
    width: 'auto',
    padding: '6px 30px 6px 10px',
    fontSize: '0.8rem',
    borderRadius: '6px',
    border: '1px solid #B2AB9C',
  },
  tableScroll: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    backgroundColor: '#2B362E',
    color: '#ffffff',
    padding: '12px 16px',
    fontSize: '0.8rem',
    fontWeight: 600,
    textAlign: 'left',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  },
  thContent: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  td: {
    padding: '12px 16px',
    fontSize: '0.85rem',
    color: '#2B362E',
    borderBottom: '1px solid #f0ece4',
  },
  stripedRow: {
    backgroundColor: 'rgba(178, 171, 156, 0.06)',
  },
  emptyCell: {
    padding: '32px 16px',
    textAlign: 'center',
    color: '#B2AB9C',
    fontSize: '0.9rem',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  actionBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6B8063',
    padding: '4px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderTop: '1px solid #f0ece4',
    flexWrap: 'wrap',
    gap: '8px',
  },
  pageInfo: {
    fontSize: '0.8rem',
    color: '#6b6b6b',
  },
  pageButtons: {
    display: 'flex',
    gap: '4px',
  },
  pageBtn: {
    padding: '6px 12px',
    fontSize: '0.8rem',
    border: '1px solid #e0dcd4',
    borderRadius: '6px',
    background: '#ffffff',
    color: '#2B362E',
    cursor: 'pointer',
  },
  pageBtnActive: {
    backgroundColor: '#6B8063',
    color: '#ffffff',
    borderColor: '#6B8063',
  },
};
